import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { PrimaryButton, SecondaryButton } from "../../components/ui/StandardButton";
import HeroHeading from "../../components/ui/HeroHeading";
import InteractiveCard from "../../components/InteractiveCard";
import {
  CheckCircle,
  Coffee,
  Star,
  Heart,
  Snowflake,
  Wind,
  Users,
  PawPrint,
  Clock,
  Shield,
  Camera,
  Mountain,
  Moon,
  Binoculars,
  Eye,
  Thermometer,
  Compass,
} from "lucide-react";
import { colors } from "../../styles/colors";
import SeoAlternateLinks from "../../components/seo/SeoAlternateLinks";
import { buildLocalizedPath } from "../../utils/routes";
import { useResponsive } from "../../hooks/useResponsive";
import { buildAbsoluteUrl, resolveSiteUrl } from "../../utils/seo";
import LazyVideo from "../../components/LazyVideo";
import { useContent } from "../../cms/ContentContext";
import { sparHandelse } from "../../utils/spar";

const ICON_COMPONENTS = {
  CheckCircle,
  Star,
  Heart,
  Coffee,
  Snowflake,
  Wind,
  Users,
  PawPrint,
  Clock,
  Shield,
  Camera,
  Mountain,
  Moon,
  Binoculars,
  Eye,
  Thermometer,
  Compass,
};

const DEFAULT_SITE_URL = "https://coldexperience.com";
const DEFAULT_OG_IMAGE = "/og/husky-ride.jpg";

const isPlainObject = (value) =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const mergeContent = (defaults, overrides) => {
  if (overrides === undefined || overrides === null) {
    return Array.isArray(defaults) ? [...defaults] : isPlainObject(defaults) ? { ...defaults } : defaults;
  }

  if (isPlainObject(defaults) && isPlainObject(overrides)) {
    const result = { ...defaults };

    Object.keys(overrides).forEach((key) => {
      result[key] = mergeContent(defaults[key], overrides[key]);
    });

    return result;
  }

  if (Array.isArray(defaults)) {
    if (!Array.isArray(overrides) || overrides.length === 0) {
      return [...defaults];
    }

    const maxLength = Math.max(defaults.length, overrides.length);
    const merged = [];

    for (let index = 0; index < maxLength; index += 1) {
      const defaultValue = defaults[index];
      const overrideValue = overrides[index];

      if (overrideValue === undefined) {
        merged[index] = mergeContent(defaultValue, undefined);
        continue;
      }

      if (isPlainObject(defaultValue) && isPlainObject(overrideValue)) {
        merged[index] = mergeContent(defaultValue, overrideValue);
      } else {
        merged[index] = overrideValue;
      }
    }

    return merged;
  }

  if (isPlainObject(overrides) || Array.isArray(overrides)) {
    return mergeContent(overrides, undefined);
  }

  return overrides;
};

const resolveIcon = (name) => {
  if (!name) {
    return null;
  }

  return ICON_COMPONENTS[name] || null;
};

export const createExperiencePage = ({ translationKey, defaultContent, routeKey }) => {
  if (!translationKey) {
    throw new Error("createExperiencePage requires a translationKey");
  }

  if (!defaultContent) {
    throw new Error("createExperiencePage requires defaultContent");
  }

  if (!routeKey) {
    throw new Error("createExperiencePage requires a routeKey");
  }

  const ExperiencePage = () => {
    const { t, i18n } = useTranslation();
    const { isMobile } = useResponsive();

    // Att någon öppnar en upplevelse är det första riktiga intresset på sajten.
    React.useEffect(() => {
      sparHandelse("upplevelse_visad", { upplevelse: routeKey || translationKey });
    }, []);

    const { getMedia } = useContent();
    const translation = t(`pages.${translationKey}`, {
      returnObjects: true,
      defaultValue: {},
    });

    const content = mergeContent(defaultContent, translation);
    const getPath = (target) => buildLocalizedPath(i18n.language, target);

    const siteUrl = resolveSiteUrl(
      content?.meta?.siteUrl || defaultContent?.meta?.siteUrl || DEFAULT_SITE_URL
    );

    const canonicalPath =
      content?.meta?.canonicalPath || buildLocalizedPath(i18n.language, routeKey);
    const canonicalUrl = buildAbsoluteUrl(siteUrl, canonicalPath);

    const ogImageRelative = content?.meta?.ogImage || DEFAULT_OG_IMAGE;
    const ogImage = ogImageRelative.startsWith("http")
      ? ogImageRelative
      : buildAbsoluteUrl(siteUrl, ogImageRelative);

    const twitterImageRelative = content?.meta?.twitterImage || ogImageRelative;
    const twitterImage = twitterImageRelative.startsWith("http")
      ? twitterImageRelative
      : buildAbsoluteUrl(siteUrl, twitterImageRelative);

    const mergedJsonLd = content?.meta?.jsonLd
      ? mergeContent(defaultContent?.meta?.jsonLd, content.meta.jsonLd)
      : defaultContent?.meta?.jsonLd
        ? mergeContent(defaultContent.meta.jsonLd, undefined)
        : null;

    if (mergedJsonLd) {
      mergedJsonLd.url = mergedJsonLd.url || canonicalUrl;
    }

    const mediaBase = content.media || {};
    // Apply live CMS media overrides (keyed as pages.<translationKey>.media.*)
    const cmsPrefix = `pages.${translationKey}.media`;
    const media = {
      ...mediaBase,
      videoSrc: getMedia(`${cmsPrefix}.videoSrc`, mediaBase.videoSrc),
      poster: getMedia(`${cmsPrefix}.poster`, mediaBase.poster),
      introVideo: getMedia(`${cmsPrefix}.introVideo`, mediaBase.introVideo),
      introPoster: getMedia(`${cmsPrefix}.introPoster`, mediaBase.introPoster),
    };
    const heroScale = media.scale || 1;
    const heroSpacing = isMobile
      ? "px-4 pt-24 pb-16"
      : "px-4 sm:px-6 lg:px-8 pt-32 pb-24";

    const features = content.features || [];
    const sections = content.sections || [];
    const sectionsHeading = content?.sectionsHeading || t("shared.sectionsHeading");
    const dayProgram = content?.dayProgram?.steps || [];
    const dayProgramTitle = content?.dayProgram?.title || t("shared.dayProgram.title");

    const closing = content.closing || {
      title: t("shared.closing.title"),
      description: t("shared.closing.description"),
      primaryButton: { label: t("shared.closing.primaryButton.label"), target: "book" },
      secondaryButton: { label: t("shared.closing.secondaryButton.label"), target: "contact" },
      ratingText: t("shared.closing.ratingText"),
    };

    return (
      <div className="min-h-screen" style={{ backgroundColor: colors.primary }}>
        <SeoAlternateLinks
          routeKey={routeKey}
          canonicalPath={canonicalPath}
          siteUrl={siteUrl}
        />
        <Helmet>
          {content?.meta?.title && <title>{content.meta.title}</title>}
          {content?.meta?.description && (
            <meta name="description" content={content.meta.description} />
          )}
          {content?.meta?.keywords && <meta name="keywords" content={content.meta.keywords} />}
          {content?.meta?.ogTitle && <meta property="og:title" content={content.meta.ogTitle} />}
          {content?.meta?.ogDescription && (
            <meta property="og:description" content={content.meta.ogDescription} />
          )}
          <meta property="og:image" content={ogImage} />
          <meta property="og:url" content={canonicalUrl} />
          <meta name="twitter:image" content={twitterImage} />
          <link rel="canonical" href={canonicalUrl} />
          {mergedJsonLd && (
            <script type="application/ld+json">{JSON.stringify(mergedJsonLd)}</script>
          )}
        </Helmet>

        {/* Hero Section */}
        <section
          data-hero="true"
          className="relative min-h-screen flex items-center justify-center overflow-hidden"
          style={{ background: colors.gradients.hero }}
        >
          <div className="absolute inset-0 overflow-hidden">
            {media.videoSrc ? (
              <LazyVideo
                src={media.videoSrc}
                poster={media.poster}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0"
                videoClassName="absolute inset-0 w-full h-full object-cover"
                videoStyle={{
                  left: "50%",
                  top: "50%",
                  transform: `translate(-50%, -50%) scale(${heroScale})`,
                  transformOrigin: "center",
                }}
                posterStyle={{
                  left: "50%",
                  top: "50%",
                  transform: `translate(-50%, -50%) scale(${heroScale})`,
                  transformOrigin: "center",
                }}
                pauseWhenOutOfView
              />
            ) : (
              <img
                src={media.poster}
                alt={content?.hero?.title || "Experience hero"}
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  left: "50%",
                  top: "50%",
                  transform: `translate(-50%, -50%) scale(${heroScale})`,
                  transformOrigin: "center",
                }}
              />
            )}
            <div className="absolute inset-0" style={{ background: colors.gradients.hero }}></div>
          </div>

          <div className={`relative z-10 w-full ${heroSpacing}`}>
            <div
              className="max-w-4xl mx-auto text-center"
              style={{ color: colors.text.primary }}
            >
              {content?.hero?.title && (
                <HeroHeading
                  title={content.hero.title}
                  className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6"
                />
              )}
              {content?.hero?.description && (
                <p
                  className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 leading-relaxed"
                  style={{ color: colors.text.primary }}
                >
                  {content.hero.description}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {content?.hero?.primaryButton?.label && (
                  <PrimaryButton to={getPath(content.hero.primaryButton.target)} size="large">
                    {content.hero.primaryButton.label}
                  </PrimaryButton>
                )}
                {content?.hero?.secondaryButton?.label && (
                  <SecondaryButton to={getPath(content.hero.secondaryButton.target)} size="large">
                    {content.hero.secondaryButton.label}
                  </SecondaryButton>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Introduction & Features */}
        <section className="py-16" style={{ backgroundColor: colors.primary }}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                {content?.intro?.heading && (
                  <h2 className="text-3xl font-bold mb-6" style={{ color: colors.text.primary }}>
                    {content.intro.heading}
                  </h2>
                )}
                {Array.isArray(content?.intro?.paragraphs) &&
                  content.intro.paragraphs.map((paragraph, index) => (
                    <p
                      key={index}
                      className="text-lg mb-6"
                      style={{ color: colors.text.primary }}
                    >
                      {paragraph}
                    </p>
                  ))}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {features.map((feature, index) => {
                    const IconComponent = resolveIcon(feature.icon);

                    return (
                      <InteractiveCard key={index} className="p-4" variant="glass">
                        <div className="flex items-start space-x-3">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${colors.accent.primary}20` }}
                          >
                            {IconComponent && (
                              <IconComponent className="h-6 w-6" style={{ color: colors.accent.primary }} />
                            )}
                          </div>
                          <div>
                            <h3
                              className="font-semibold mb-1"
                              style={{ color: colors.text.primary }}
                            >
                              {feature.title}
                            </h3>
                            <p className="text-sm" style={{ color: colors.text.primary }}>
                              {feature.description}
                            </p>
                          </div>
                        </div>
                      </InteractiveCard>
                    );
                  })}
                </div>
              </div>

              <div className="relative rounded-lg shadow-lg overflow-hidden min-h-[320px]">
                {media.introVideo || media.videoSrc ? (
                  <LazyVideo
                    src={media.introVideo || media.videoSrc}
                    poster={media.introPoster || media.poster}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    className="absolute inset-0"
                    videoClassName="absolute inset-0 h-full w-full object-cover"
                    pauseWhenOutOfView
                  />
                ) : (
                  <img
                    src={media.introPoster || media.poster}
                    alt={content?.intro?.heading || content?.hero?.title || "Experience"}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Highlight Sections */}
        <section className="py-16" style={{ backgroundColor: colors.secondary }}>
          <div className="max-w-6xl mx-auto px-4">
            <h2
              className="text-3xl font-bold text-center mb-12"
              style={{ color: colors.text.primary }}
            >
              {sectionsHeading}
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              {sections.map((section, sectionIndex) => {
                const IconComponent = resolveIcon(section.icon) || CheckCircle;

                return (
                  <div key={sectionIndex}>
                    {section?.title && (
                      <h3
                        className="text-xl font-semibold mb-6"
                        style={{ color: colors.accent.primary }}
                      >
                        {section.title}
                      </h3>
                    )}
                    <ul className="space-y-3">
                      {section?.items?.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start space-x-3">
                          <IconComponent
                            className="h-5 w-5 mt-1 flex-shrink-0"
                            style={{ color: colors.accent.primary }}
                          />
                          <span style={{ color: colors.text.primary }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Day Program */}
        <section className="py-16" style={{ backgroundColor: colors.primary }}>
          <div className="max-w-6xl mx-auto px-4">
            {dayProgramTitle && (
              <h2 className="text-3xl font-bold text-center mb-12" style={{ color: colors.text.primary }}>
                {dayProgramTitle}
              </h2>
            )}

            <div className="grid md:grid-cols-4 gap-6">
              {dayProgram.map((step, index) => {
                const IconComponent = resolveIcon(step.icon);

                return (
                  <div key={index} className="text-center">
                    <div
                      className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: colors.accent.primary }}
                    >
                      {IconComponent && <IconComponent className="h-8 w-8 text-white" />}
                    </div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.text.primary }}>
                      {step.title}
                    </h3>
                    <p className="text-sm" style={{ color: colors.text.primary }}>
                      {step.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section
          className="py-20 text-center text-white relative"
          style={{ background: colors.gradients.hero }}
        >
          <div className="max-w-4xl mx-auto px-4">
            {closing?.title && <h2 className="text-4xl font-bold mb-6">{closing.title}</h2>}
            {closing?.description && (
              <p className="text-xl mb-8" style={{ color: colors.text.primary }}>
                {closing.description}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {closing?.primaryButton?.label && (
                <PrimaryButton to={getPath(closing.primaryButton.target)} size="large">
                  {closing.primaryButton.label}
                </PrimaryButton>
              )}
              {closing?.secondaryButton?.label && (
                <SecondaryButton to={getPath(closing.secondaryButton.target)} size="large">
                  {closing.secondaryButton.label}
                </SecondaryButton>
              )}
            </div>

            {(closing?.showStarRating || closing?.ratingText) && (
              <div className="mt-8 flex justify-center items-center space-x-4 text-sm">
                {closing?.showStarRating && (
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: closing?.starCount || 5 }).map((_, index) => (
                      <Star key={index} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                )}
                {closing?.ratingText && <span>{closing.ratingText}</span>}
              </div>
            )}
          </div>
        </section>
      </div>
    );
  };

  ExperiencePage.displayName = `ExperiencePage(${translationKey})`;

  return ExperiencePage;
};

export default createExperiencePage;
